import { DefaultContainerProps } from 'app/infrastructure/redux';
import { ComplexValue, TextCollection } from 'app/types-shared';
import { FIELD_TYPE } from 'app/infrastructure/simple-form';
import { ExtraFooterButton, OptionsType } from 'ui/common/select/types';
import { SimpleFormValidation, SimpleFormValidationProp } from './validation';
import { MeatballPanelConfiguration, ValidationProps } from 'ui';

export type SimpleFormContainerProps = SimpleFormContainerInternalProps & SimpleFormComponentProps
  & SimpleFormContainerState & DefaultContainerProps;

export interface SimpleFormContainerState {
  loadedOptions: object;
}

export enum ApiToUse {
  API,
  ADMINAPI
}

export const SerializeType = {
  PRIMITIVE_VALUE: 'PRIMITIVE_VALUE',
  V_VALUE: 'V_VALUE',
  MULTIPLE_V_VALUES: 'MULTIPLE_V_VALUES',
  VALUE_PAIRS: 'VALUE_PAIRS'
};

export type SimpleFormSerialize = {
  [key: string]: string;
  type: string;
  formName?: string;
};

export type SerializeMapping = {
  [key: string]: (storeValue: any, currentWireOutput: FormWireItem, options: SimpleFormSerialize, allValues: any) => any;
};

export interface SimpleFormValuesStoreData {
  [key: string]: {
    value: any;
  };
}

export interface SimpleFormEditingStoreData {
  values: SimpleFormValuesStoreData;
  persistedValues: SimpleFormValuesStoreData;
  validation: any;
  hasChanges: boolean;
}

export interface SimpleFormComponentStoreData {
  [name: string]: {
    componentType: FIELD_TYPE;
    serialize: SimpleFormSerialize;
    validationProps?: SimpleFormValidationProp[];
  };
}

export interface ComponentAsyncData {
  data: ComplexValue<any>[];
  /**
   * Arguments used to fetch, to know if load conditions has changed
   */
  args: { [key: string]: any };
  /**
   * true if data is fetching for this individual component
   */
  fetching: boolean;
  /**
   * true if data is fetching for this individual component
   */
  value: any;
  /**
   * true if error occured while loading. components display a generic error message.
   */
  error: boolean | null;
}
export interface SimpleFormTempdataState {
  options: {
    [actionContext: string]: ComponentAsyncData;
  };
  categoryOptions: {
    [actionContext: string]: ComponentAsyncData;
  };
}

export interface SimpleFormAreaState {
  editing: SimpleFormEditingStoreData;
  tempdata: FormOptions;
  componentData: SimpleFormComponentStoreData;
}

export interface SimpleFormState {
  simpleForm: SimpleFormAreaState;
  validation?: any;
  texts?: any;
}

export interface SimpleFormContainerInternalProps {
  simpleForm: SimpleFormAreaState;
  validation: any;
}

export interface ValueToStoreData { [name: string]: { value: string | number | object } }

export interface TextLabel {
  text: string | JSX.Element;
  label?: string;
  required?: boolean;
}

export interface InitialValueObject {
  value: number;
  description: string;
}

export interface SimpleFormComponentProps {
  /**
   * Fields defined in field-types.ts
   *  
   */
  type: FIELD_TYPE;

  /**
   * Name - will be used as a property name in store
   *  
   */
  name: string;

  /**
   * Will be injected to the store as the initial value
   *  
   */
  initialValue: any;

  label?: TextLabel;

  /**
   * Endpoint for requesting options for Select components
   *  
   */
  optionsEndpoint?: string;
  optionsParams?: any;
  optionsDisplaySetting?: OptionsType;

  /**
   * Collection of options
   *  
   */
  staticOptions?: any;

  /**
   * True - component will be loaded as open
   *  
   */
  hasFocus?: boolean;

  /**
   * Translations for Select components
   *  
   */
  translation?: any;
  placeholder?: string;

  /**
   * Configuration of DateTime component
   *  
   */
  locale?: string;
  useTime?: boolean;

  /**
   * Configuration of Multiselect component
   */
  displaySelectAll?: boolean;
  includeDisabledLabel?: string;

  className?: string;
  readOnly?: boolean;
  readOnlyLabel?: string;

  apiToUse?: ApiToUse;
  descriptionAllLevels?: boolean;
  initiallyShowLongDescription?: boolean;
  serialize?: SimpleFormSerialize;
  validationProps?: SimpleFormValidationProp[];

  serverValidation?: ValidationProps;
  clearServerValidation?: (value: any) => void;

  maxLength?: number;
  maxValue?: number;
  minValue?: number;
  maxDecimals?: number;
  errorMessage?: string;
  infoText?: string;

  alwaysOpen?: boolean;
  searchRequired?: boolean;
  resetPreviousValueWhenEmpty?: boolean;
  categoryId?: number;

  levelsNotAllowed?: boolean;

  textPageOf?: string;
  showIndexWhenNoOfPagesIsAbove?: number;

  extraFooterButton?: ExtraFooterButton;
  displayCategories?: boolean;
  categoryOptionsEndpoint?: string;
  categoryOptionsParams?: object;
  meatballPanelConfiguration?: MeatballPanelConfiguration;
}

export type FormValuesType = { [key: string]: number | string | ComplexValue<number> | ComplexValue<number>[] };

export interface SimpleFormStateProps {
  formValues: FormValuesType;
  serialize: (serializeMapping: SerializeMapping) => FormWireData;
  validation: { [key: string]: ValidationProps };
  formOptions: FormOptions;
}

export interface FormOptions {
  options: FormOptionsCollection;
  categoryOptions: FormOptionsCollection;
}

interface FormOptionsCollection {
  [optionKey: string]: FormOption;
}

export interface FormOption {
  data: ComplexValue<number>[];
  args: any;
  value: number;
  fetching: boolean;
  error?: boolean;
}

export interface SimpleFormDispatchProps {
  validate: SimpleFormValidation;
}

export type SimpleFormProps = SimpleFormStateProps & SimpleFormDispatchProps;

export interface SimpleFormMethods {
  getProperty: (name: string) => any;
  getOptions: (name: string) => any;
  getCategoryOptions: (name: string) => any;
  getValidation: (name: string) => any;
  onLoadOptions: (refName: string, optionsEndpoint: string, optionsParams: any) => any;
  onLoadCategoryOptions: (refName: string, optionsEndpoint: string, optionsParams: any) => any;
  handleChange: (name: string) => any;
  isFetching: (name: string) => any;
  createRefToScrollableComponent: (name: string) => any;
  buildLabel: (label: TextLabel) => any;
}

export interface SimpleFieldFactoryParameters {
  displayNotSelectedOption?: boolean;
}

export interface SimpleFormParameters {
  texts?: TextCollection;
}

export type FormWireValue = { v: number };
export type FormWireArray = [
  { [key: string]: FormWireValue }
];

export type FormWireItem = number | string | FormWireValue | FormWireArray;

export interface FormWireData {
  [key: string]: FormWireItem;
}
